import type { PropsWithoutRef, ReactElement, ReactNode, Ref } from 'react';
import { forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { readableColor } from 'polished';

import {
  useBreakpoint,
  useConfiguration,
  useConsolidatedRef,
  useElement,
  useI18n,
  useScrollStick,
  useTheme
} from '../../hooks';
import type { ScrollStickOptions } from '../../hooks/useScrollStick';
import { defaultThemeProp } from '../../theme';
import type {
  AsProp,
  BaseProps,
  ForwardRefForwardPropsComponent,
  NoChildrenProp,
  OmitStrict
} from '../../types';
import { focusHeadingOrContainer } from '../../utils';
import AppShellContext from '../AppShell/AppShellContext';
import { StyledBanner } from '../Banner/Banner.styles';
import type { BreadcrumbsProps } from '../Breadcrumbs';
import Breadcrumbs, { StyledBreadcrumbs } from '../Breadcrumbs';
import Flex from '../Flex';
import Grid from '../Grid';
import Icon from '../Icon';
import SummaryItem from '../SummaryItem';
import type { Tab, TabsProps } from '../Tabs';
import Tabs, { TabPanel } from '../Tabs';
import { StyledTabPanel } from '../Tabs/TabPanel';
import { StyledAllTabsWrapper, StyledTabs, StylesAllTabsButtonWrapper } from '../Tabs/Tabs.styles';
import Text from '../Text';
import { StyledMenuButton } from '../MenuButton/MenuButton';

export interface PageTemplateProps extends BaseProps, AsProp, NoChildrenProp {
  title: string;
  icon?: string;
  actions?: ReactNode;
  banners?: ReactNode;
  path?: BreadcrumbsProps['path'];
  header?: ReactNode;
  utilities?: ReactNode;
  scrollContent?: boolean;
  ref?: Ref<HTMLDivElement>;
}

export interface TabbedPageTab extends Tab {
  content: ReactNode;
  /**
   * Determines layout spacing.
   * @default 'standard'
   */
  layout?: 'standard' | 'full-size';
}

export interface TabbedPageProps extends OmitStrict<PageTemplateProps, 'forwardedAs'> {
  tabs: OmitStrict<TabsProps, 'tabs' | 'currentTabId'> & {
    tabs: TabbedPageTab[];
  } & (
      | { defaultTabId?: string; currentTabId?: never }
      | { defaultTabId?: never; currentTabId: string }
    );
}

export interface DynamicLayoutPageProps extends OmitStrict<PageTemplateProps, 'scrollContent'> {
  a: ReactNode;
}

export interface OneColumnPageProps extends PageTemplateProps {
  a: ReactNode;
}

export interface TwoColumnPageProps extends PageTemplateProps {
  a: ReactNode;
  b: ReactNode;
}

export interface ThreeColumnPageProps extends PageTemplateProps {
  a: ReactNode;
  b: ReactNode;
  c: ReactNode;
}

export interface FourColumnPageProps extends PageTemplateProps {
  a: ReactNode;
  b: ReactNode;
  c: ReactNode;
  d: ReactNode;
}

export interface WideNarrowPageProps extends PageTemplateProps {
  a: ReactNode;
  b: ReactNode;
}

export interface NarrowWidePageProps extends PageTemplateProps {
  a: ReactNode;
  b: ReactNode;
}

export interface WideWideNarrowPageProps extends PageTemplateProps {
  a: ReactNode;
  b: ReactNode;
  c: ReactNode;
}

export interface NarrowWideWidePageProps extends PageTemplateProps {
  a: ReactNode;
  b: ReactNode;
  c: ReactNode;
}

export interface NarrowWideNarrowPageProps extends PageTemplateProps {
  a: ReactNode;
  b: ReactNode;
  c: ReactNode;
}

export interface PageLayoutProps extends PageTemplateProps {
  tabs?: ReactElement<typeof Tabs>;
  regions: ReactNode[];
  regionsRef?: Ref<HTMLDivElement>;
  cols?: string;
  padContent?: boolean;
}

export const StyledPageIcon = styled(Flex)(({ theme }) => {
  const bgColor = theme.components['case-view'].header['background-color'];
  const color =
    theme.components['case-view'].header['foreground-color'] !== 'auto'
      ? theme.components['case-view'].header['foreground-color']
      : readableColor(bgColor);
  return css`
    --brand-accent: ${theme.base.palette['brand-accent']};
    --gradient-start: oklch(from var(--brand-accent) calc(l - 0.3) calc(c * 1.5) h);
    --gradient-mid: oklch(from var(--brand-accent) l c h);
    --gradient-end: oklch(from var(--brand-accent) calc(l + 0.5) calc(c * 1.5) h);
    height: calc(4 * ${theme.base.spacing});
    width: calc(4 * ${theme.base.spacing});
    color: ${theme.components['case-view'].icon.color !== 'auto'
      ? theme.components['case-view'].icon.color
      : color};
    background: ${theme.components['case-view'].icon.background !== 'auto'
      ? theme.components['case-view'].icon.background
      : bgColor};
    box-shadow: ${theme.components['case-view'].icon['box-shadow']};
    border-radius: calc(0.5 * ${theme.base['border-radius']});
  `;
});

StyledPageIcon.defaultProps = defaultThemeProp;

export const StyledPageHeader = styled.header(({ theme }) => {
  const appFg = theme.base.palette['app-foreground'];
  const foregroundColor = theme.base.palette['foreground-color'];
  const tabsDetached = theme.components.tabs.detached;
  const color = appFg !== 'auto' ? appFg : foregroundColor;

  return css`
    h1 {
      color: ${color};
    }

    h1:focus-visible {
      outline: transparent;
      box-shadow: ${theme.base.shadow.focus};
    }

    ${StyledBreadcrumbs} {
      margin-block-end: ${theme.base.spacing};
    }

    ${StyledBanner}, ${StyledTabs}, ${StylesAllTabsButtonWrapper} {
      margin-block-start: calc(2 * ${theme.base.spacing});
    }

    ${tabsDetached &&
    css`
      ${StyledAllTabsWrapper} {
        margin-block-start: calc(2 * ${theme.base.spacing});
        border-radius: var(--border-radius, ${theme.base['border-radius']});
        border-end-start-radius: 0;

        ${StyledTabs} {
          border-radius: unset;
          border-start-end-radius: inherit;
          margin-block-start: 0;
        }

        ${StylesAllTabsButtonWrapper} {
          margin-block-start: 0;

          ${StyledMenuButton} {
            border-start-start-radius: var(--border-radius, ${theme.base['border-radius']});
          }
        }
      }
    `}
  `;
});

StyledPageHeader.defaultProps = defaultThemeProp;

export const StyledRegion = styled.div<{ renderSingleRegion?: boolean }>(
  ({ renderSingleRegion }) => {
    if (renderSingleRegion) {
      return css`
        height: 100%;
      `;
    }
  }
);

StyledRegion.defaultProps = defaultThemeProp;

export const StyledRegions = styled.div<{ pad: boolean }>(({ pad, theme }) => {
  const spacing = theme.base.spacing;

  return css`
    flex-grow: 1;
    max-width: 100%;

    ${pad &&
    css`
      padding: 0 calc(2 * ${spacing}) calc(2 * ${spacing});

      @media (width < ${theme.base.breakpoints.sm}) {
        padding: 0 calc(0.75 * ${spacing}) calc(2 * ${spacing});
      }
    `}
    [role='tabpanel'] & {
      padding: 0;
    }
  `;
});
StyledRegions.defaultProps = defaultThemeProp;

export const StyledContent = styled.div``;
StyledContent.defaultProps = defaultThemeProp;

export const StyledScrollableTabPanel: typeof TabPanel = styled(TabPanel)(() => {
  return css`
    height: 100%;
    overflow: auto;
  `;
});

StyledScrollableTabPanel.defaultProps = defaultThemeProp;

export const StyledPageLayout = styled.div<{
  scrollContent: PageLayoutProps['scrollContent'];
  minContentHeight?: number;
}>(
  ({
    theme: {
      base: {
        palette: { 'app-background': appBackground }
      }
    },
    scrollContent,
    minContentHeight
  }) => {
    const emptyContentHeight = minContentHeight ? `${minContentHeight}px` : undefined;

    return css`
      --content-height-in-view: ${emptyContentHeight};
      position: relative;
      min-height: inherit;
      background: ${appBackground};

      ${scrollContent &&
      css`
        ${StyledContent} {
          position: relative;
        }

        ${StyledRegions} {
          position: absolute;
          height: 100%;
          width: 100%;
          overflow: auto;
        }
      `}
    `;
  }
);

StyledPageLayout.defaultProps = defaultThemeProp;

const StyledPageLayoutWrapper = styled.div`
  height: 100%;
`;

const StylePageTabContent = styled.div`
  ${StyledRegions} {
    padding: 0;
  }
`;

const PageLayout: ForwardRefForwardPropsComponent<PageLayoutProps> = forwardRef(function PageLayout(
  {
    regions,
    cols = `repeat(${regions.length}, minmax(0, 1fr))`,
    title,
    icon,
    path,
    actions,
    banners,
    tabs,
    utilities,
    as,
    header,
    regionsRef,
    scrollContent = false,
    padContent = true,
    ...restProps
  }: PropsWithoutRef<PageLayoutProps>,
  ref: PageLayoutProps['ref']
) {
  const t = useI18n();
  const [contentEl, setContentEl] = useElement<HTMLElement>();
  const isMediumOrAbove = useBreakpoint('md');
  const hasSideBySideColumns = regions.length > 1 && isMediumOrAbove;
  const { headerEl } = useContext(AppShellContext);
  const { loadedRef } = useConfiguration();
  const [panel, setPanel] = useState('content');
  const {
    components: {
      tabs: { detached: tabsDetached }
    }
  } = useTheme();

  const pageRef = useConsolidatedRef(ref);
  const pageTabsRef = useRef<HTMLElement>(null);
  const consolidatedRegionRef = useConsolidatedRef(regionsRef);
  const scrollStickOptions = useRef<ScrollStickOptions>({
    elements: []
  });

  scrollStickOptions.current.scrollContainer = scrollContent && contentEl ? contentEl : undefined;
  scrollStickOptions.current.offset = (headerEl?.offsetHeight ?? 0) + 16;

  useScrollStick(hasSideBySideColumns ? scrollStickOptions.current : undefined);

  const renderSingleRegion = regions?.length === 1;

  const [minContentHeight, setMinContentHeight] = useState<number | undefined>();

  useEffect(() => {
    if (loadedRef.current) {
      if (pageRef.current && title) {
        focusHeadingOrContainer(pageRef.current, title);
      }
    } else {
      loadedRef.current = true;
    }
  }, [title]);

  useEffect(() => {
    if (consolidatedRegionRef.current) {
      const refElement = consolidatedRegionRef.current;
      const regionsPaddingBottomPixels = Number.parseInt(
        window.getComputedStyle(refElement).getPropertyValue('padding-bottom'),
        10
      );

      const resizeObserver = new ResizeObserver(() => {
        const headerOffset = headerEl?.offsetHeight ?? 0;
        const emptyHeight =
          document.documentElement.offsetHeight -
          (headerOffset + refElement.offsetTop) -
          regionsPaddingBottomPixels;

        setMinContentHeight(emptyHeight);
      });

      resizeObserver.observe(refElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [consolidatedRegionRef, headerEl]);

  const pageContent = (
    <Flex
      container
      item={{ grow: 1 }}
      as={StyledContent}
      ref={scrollContent ? setContentEl : undefined}
    >
      {renderSingleRegion && (
        <StyledRegions ref={consolidatedRegionRef} pad={padContent}>
          <Flex
            container={{
              direction: 'column',
              gap: 2
            }}
            as={StyledRegion}
            ref={(el: HTMLDivElement | null) => {
              if (el) {
                scrollStickOptions.current.elements[0] = el;
              }
            }}
            renderSingleRegion
          >
            {regions[0]}
          </Flex>
        </StyledRegions>
      )}

      {!renderSingleRegion && (
        <Grid
          container={{
            cols: 'minmax(0, 1fr)',
            alignItems: 'start',
            gap: 2,
            pad: [0, 0.75, 2]
          }}
          sm={{ container: { pad: [0, 2, 2] } }}
          md={{ container: { cols } }}
          as={StyledRegions}
          ref={consolidatedRegionRef}
        >
          {regions.map((colItems, i) => {
            return (
              <Flex
                container={{
                  direction: 'column',
                  gap: 2
                }}
                as={StyledRegion}
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                ref={(el: HTMLDivElement | null) => {
                  if (el) {
                    scrollStickOptions.current.elements[i] = el;
                  }
                }}
              >
                {colItems}
              </Flex>
            );
          })}
        </Grid>
      )}
    </Flex>
  );

  const pageTabs = [
    { name: t('content'), id: 'content', content: pageContent },
    {
      name: t('chat_with_ai'),
      id: 'chat_with_ai',
      content: utilities
    }
  ];

  const pageHeader =
    !!path || !!icon || !!actions || !!title || !!banners || !!tabs || !!header ? (
      <Flex
        container={{ direction: 'column', pad: [1.5, 0.75, 0] }}
        sm={{ container: { pad: [1.5, 2, 0] } }}
        item={{ grow: 0, shrink: 0 }}
        as={StyledPageHeader}
      >
        {path && path.length > 0 && <Breadcrumbs path={path} />}

        {header || (
          <SummaryItem
            visual={
              icon && (
                <StyledPageIcon container={{ alignItems: 'center', justify: 'center' }}>
                  <Icon name={icon} />
                </StyledPageIcon>
              )
            }
            primary={<Text variant='h1'>{title}</Text>}
            actions={actions}
          />
        )}

        {banners}
        {tabs}
      </Flex>
    ) : null;

  const handleTabChange = (id: string) => {
    setPanel(id);
  };

  if (utilities) {
    if (isMediumOrAbove || tabs) {
      return (
        <Flex container={{ gap: 2 }} as={StyledPageLayoutWrapper}>
          <Flex
            {...restProps}
            container={{
              direction: 'column',
              alignItems: 'stretch',
              gap: 2
            }}
            item={{ grow: 1 }}
            scrollContent={scrollContent}
            as={StyledPageLayout}
            forwardedAs={as}
            ref={pageRef}
            minContentHeight={minContentHeight}
          >
            {pageHeader}
            {pageContent}
          </Flex>
          {(isMediumOrAbove || !tabs) && <div>{utilities}</div>}
        </Flex>
      );
    }

    return (
      <Flex
        {...restProps}
        as={StyledPageLayout}
        forwardedAs={as}
        ref={pageRef}
        container={{ direction: 'column' }}
      >
        {pageHeader}
        <Flex
          container={{ direction: 'column', pad: [2, 0.75, 0], gap: tabsDetached ? undefined : 2 }}
          sm={{ container: { pad: [2, 2, 0] } }}
        >
          <Tabs
            tabs={pageTabs}
            onTabClick={handleTabChange}
            currentTabId={panel}
            ref={pageTabsRef}
          />
          {pageTabs.map(tab => (
            <TabPanel tabId={tab.id} currentTabId={panel} tablistRef={pageTabsRef} key={tab.id}>
              <StylePageTabContent>{tab.content}</StylePageTabContent>
            </TabPanel>
          ))}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      {...restProps}
      container={{
        direction: 'column',
        alignItems: 'stretch',
        gap: 2
      }}
      item={{ grow: 1 }}
      scrollContent={scrollContent}
      as={StyledPageLayout}
      forwardedAs={as}
      ref={pageRef}
      minContentHeight={minContentHeight}
    >
      {pageHeader}
      {pageContent}
    </Flex>
  );
});

const StyledTabbedPage = styled(PageLayout)<{ $layout?: TabbedPageTab['layout'] }>(({
  $layout = 'standard',
  theme: {
    base: {
      palette: { 'border-line': borderLine }
    },
    components: {
      tabs: { detached: tabsDetached }
    }
  }
}) => {
  return css`
    gap: 0;

    ${tabsDetached &&
    css`
      ${StyledTabPanel} {
        border-block-end: none;
        box-shadow: 0 0.0625rem 0 ${borderLine};
      }
    `}

    ${$layout === 'full-size' &&
    css`
      ${StyledRegions} {
        padding: 0;

        ${StyledTabPanel} {
          padding-block-start: 0;
        }
      }
    `}
  `;
});

StyledTabbedPage.defaultProps = defaultThemeProp;

export const TabbedPage: ForwardRefForwardPropsComponent<TabbedPageProps> = forwardRef(
  function TabbedPage(
    { tabs: tabsProps, utilities, as, ...restProps }: PropsWithoutRef<TabbedPageProps>,
    ref: TabbedPageProps['ref']
  ) {
    const isMediumOrAbove = useBreakpoint('md');

    const { currentTabId } = tabsProps;

    const t = useI18n();

    const tabs = useMemo(() => {
      if (utilities && !isMediumOrAbove) {
        return {
          ...tabsProps,
          tabs: [
            ...tabsProps.tabs,
            { id: 'chat_with_ai', name: t('chat_with_ai'), content: utilities }
          ]
        };
      }
      return tabsProps;
    }, [tabsProps, utilities, isMediumOrAbove]);

    const [tabId, setTabId] = useState(tabs.defaultTabId || tabs.tabs[0]?.id);

    useEffect(() => {
      if (!currentTabId) {
        const activeTabId = tabs.defaultTabId || tabs.tabs[0]?.id;
        if (activeTabId && tabId === 'agent') setTabId(activeTabId);
      }
    }, [tabsProps, isMediumOrAbove]);

    const tabPanels = tabs.tabs.map(tab => {
      return (
        <Flex
          container={{ direction: 'column', gap: 2 }}
          as={TabPanel}
          tabId={tab.id}
          currentTabId={currentTabId ?? tabId}
          key={tab.id}
        >
          {tab.content}
        </Flex>
      );
    });

    return (
      <StyledTabbedPage
        {...restProps}
        utilities={utilities}
        tabs={
          <Tabs
            {...tabs}
            onTabClick={(id, e) => {
              if (!currentTabId) {
                setTabId(id);
              }
              tabs.onTabClick?.(id, e);
            }}
            currentTabId={currentTabId ?? tabId}
          />
        }
        regions={[<>{tabPanels}</>]}
        $layout={
          tabs.tabs.find(({ id }) => {
            const activeTabId = currentTabId ?? tabId;
            return id === activeTabId;
          })?.layout
        }
        forwardedAs={as}
        ref={ref}
      />
    );
  }
);

export const DynamicLayoutPage: ForwardRefForwardPropsComponent<DynamicLayoutPageProps> =
  forwardRef(function DynamicLayoutPage(
    { a, ...restProps }: PropsWithoutRef<DynamicLayoutPageProps>,
    ref: DynamicLayoutPageProps['ref']
  ) {
    return <PageLayout regions={[a]} ref={ref} {...restProps} padContent={false} />;
  });

export const OneColumnPage: ForwardRefForwardPropsComponent<OneColumnPageProps> = forwardRef(
  function OneColumnPage(
    { a, ...restProps }: PropsWithoutRef<OneColumnPageProps>,
    ref: OneColumnPageProps['ref']
  ) {
    return <PageLayout regions={[a]} ref={ref} {...restProps} cols='minmax(0, 1fr)' />;
  }
);

export const TwoColumnPage: ForwardRefForwardPropsComponent<TwoColumnPageProps> = forwardRef(
  function TwoColumnPage(
    { a, b, ...restProps }: PropsWithoutRef<TwoColumnPageProps>,
    ref: TwoColumnPageProps['ref']
  ) {
    return (
      <PageLayout regions={[a, b]} ref={ref} {...restProps} cols='repeat(2, minmax(0, 1fr))' />
    );
  }
);

export const ThreeColumnPage: ForwardRefForwardPropsComponent<ThreeColumnPageProps> = forwardRef(
  function ThreeColumnPage(
    { a, b, c, ...restProps }: PropsWithoutRef<ThreeColumnPageProps>,
    ref: ThreeColumnPageProps['ref']
  ) {
    return (
      <PageLayout regions={[a, b, c]} ref={ref} {...restProps} cols='repeat(3, minmax(0, 1fr))' />
    );
  }
);

export const FourColumnPage: ForwardRefForwardPropsComponent<FourColumnPageProps> = forwardRef(
  function FourColumnPage(
    { a, b, c, d, ...restProps }: PropsWithoutRef<FourColumnPageProps>,
    ref: FourColumnPageProps['ref']
  ) {
    return (
      <PageLayout
        regions={[a, b, c, d]}
        ref={ref}
        {...restProps}
        cols='repeat(4, minmax(0, 1fr))'
      />
    );
  }
);

export const WideNarrowPage: ForwardRefForwardPropsComponent<WideNarrowPageProps> = forwardRef(
  function WideNarrowPage(
    { a, b, ...restProps }: PropsWithoutRef<WideNarrowPageProps>,
    ref: WideNarrowPageProps['ref']
  ) {
    return (
      <PageLayout regions={[a, b]} ref={ref} {...restProps} cols='minmax(0, 2fr) minmax(0, 1fr)' />
    );
  }
);

export const NarrowWidePage: ForwardRefForwardPropsComponent<WideNarrowPageProps> = forwardRef(
  function NarrowWidePage(
    { a, b, ...restProps }: PropsWithoutRef<WideNarrowPageProps>,
    ref: NarrowWidePageProps['ref']
  ) {
    return (
      <PageLayout regions={[a, b]} ref={ref} {...restProps} cols='minmax(0, 1fr) minmax(0, 2fr)' />
    );
  }
);

export const WideWideNarrowPage: ForwardRefForwardPropsComponent<WideWideNarrowPageProps> =
  forwardRef(function WideWideNarrowPage(
    { a, b, c, ...restProps }: PropsWithoutRef<WideWideNarrowPageProps>,
    ref: WideWideNarrowPageProps['ref']
  ) {
    return (
      <PageLayout
        regions={[a, b, c]}
        ref={ref}
        {...restProps}
        cols='repeat(2, minmax(0, 2fr)) minmax(0, 1fr)'
      />
    );
  });

export const NarrowWideWidePage: ForwardRefForwardPropsComponent<NarrowWideWidePageProps> =
  forwardRef(function NarrowWideWidePage(
    { a, b, c, ...restProps }: PropsWithoutRef<NarrowWideWidePageProps>,
    ref: NarrowWideWidePageProps['ref']
  ) {
    return (
      <PageLayout
        regions={[a, b, c]}
        ref={ref}
        {...restProps}
        cols='minmax(0, 1fr) repeat(2, minmax(0, 2fr))'
      />
    );
  });

export const NarrowWideNarrowPage: ForwardRefForwardPropsComponent<NarrowWideNarrowPageProps> =
  forwardRef(function NarrowWideNarrowPage(
    { a, b, c, ...restProps }: PropsWithoutRef<NarrowWideNarrowPageProps>,
    ref: NarrowWideNarrowPageProps['ref']
  ) {
    return (
      <PageLayout
        regions={[a, b, c]}
        ref={ref}
        {...restProps}
        cols='minmax(0, 1fr) minmax(0, 3fr) minmax(0, 1fr)'
      />
    );
  });
